import React from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Shield, Lock, FileCheck, Users, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';

const CloudSecurityPage = () => {
  const features = [
    {
      icon: Shield,
      title: 'Identity & Access Management',
      description: 'Comprehensive IAM policies and role-based access control',
    },
    {
      icon: Lock,
      title: 'Data Encryption',
      description: 'End-to-end encryption for data at rest and in transit',
    },
    {
      icon: FileCheck,
      title: 'Compliance Management',
      description: 'HIPAA, PCI-DSS, SOC 2, and GDPR compliance support',
    },
    {
      icon: AlertTriangle,
      title: 'Threat Detection',
      description: '24/7 monitoring and automated threat response',
    },
    {
      icon: Users,
      title: 'Security Audits',
      description: 'Regular security assessments and vulnerability scanning',
    },
    {
      icon: CheckCircle,
      title: 'Incident Response',
      description: 'Rapid incident response and disaster recovery planning',
    },
  ];

  const compliance = ['HIPAA', 'PCI-DSS', 'SOC 2 Type II', 'GDPR', 'ISO 27001', 'FedRAMP'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#020B1A] to-[#061426]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#020B1A] via-[#061426] to-[#0A1E3F] overflow-hidden text-white pt-32 pb-24 mt-16">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-cyan-500/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-600/30 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Cloud Security & Compliance
            </h1>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              Enterprise-grade security architecture meeting the highest compliance standards. Protect your cloud infrastructure with our comprehensive security solutions.
            </p>
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-medium px-8 py-6 text-lg transition-all duration-300" style={{ boxShadow: '0 0 30px rgba(0, 212, 255, 0.5)' }}>
                Get Security Assessment
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-b from-[#061426] to-[#020B1A]">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Comprehensive Security Solutions</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Multi-layered security approach to protect your cloud assets
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-gradient-to-b from-[#061426] to-[#020B1A] border-2 border-cyan-500/30 rounded-xl p-8 transition-all duration-300 hover:border-cyan-400 hover:shadow-xl">
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 bg-gradient-to-b from-[#020B1A] to-[#061426]">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Compliance Standards</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              We help you meet and maintain critical compliance requirements
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {compliance.map((standard, index) => (
              <div key={index} className="bg-gradient-to-b from-[#061426] to-[#020B1A] rounded-lg p-6 text-center shadow-md hover:shadow-xl transition-shadow">
                <div className="text-2xl font-bold text-cyan-400">{standard}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#020B1A] via-[#061426] to-[#0A1E3F] overflow-hidden text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Secure Your Cloud Infrastructure Today
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto">
            Get a comprehensive security audit from our certified experts
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-medium px-10 py-6 text-lg transition-all duration-300" style={{ boxShadow: '0 0 30px rgba(0, 212, 255, 0.5)' }}>
              Schedule Security Consultation
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CloudSecurityPage;